// Uitslagen in TRS zijn vrije tekst. Een rit levert een afstand ("96.59"), een
// Full Pull, of een status zoals DNS of HP. Dit bestand maakt daar één type van.
//
// Wedstrijdopzet: iedereen in een klasse rijdt eerst een kwalificatierit. De
// besten mogen terugkomen voor de finale, en díe rit bepaalt de einduitslag.
// In de Farm Stock-tabel staat de kwalificatie in run1 (soms een tweede poging
// in run2) en de finale in run3; de GN/Trucks/Nationaal-tabel heeft daar een
// eigen kolom `finale` voor. Hoeveel rijders de finale halen verschilt per
// klasse - meestal vier, maar twee tot acht komt allemaal voor - dus daar mag
// nooit op geprogrammeerd worden. Bepalend is of er een finale-uitslag staat.

export type Resultaat =
  | { soort: "afstand"; meters: number; tekst: string }
  | { soort: "fullpull"; tekst: string }
  | { soort: "status"; tekst: string; uitleg: string };

// Statuscodes zoals ze in TRS voorkomen, met de schrijfwijzen door elkaar.
const STATUS: Record<string, { tekst: string; uitleg: string }> = {
  DNS: { tekst: "DNS", uitleg: "Did not start - niet gestart" },
  DNF: { tekst: "DNF", uitleg: "Did not finish - rit niet uitgereden" },
  // Uit het reglement 2026, hoofdstuk 21 A2. Op de uitslag na DIS en voor DNS.
  HP: {
    tekst: "HP",
    uitleg: "Hookpoints - de trekpoging is vervallen, maar de machine trok de ketting wel zelf strak en krijgt punten",
  },
  // NTTO schrijft dit op ntto.nl/live als DIS; die term kennen de fans al.
  DIS: { tekst: "DIS", uitleg: "Gediskwalificeerd" },
  DISK: { tekst: "DIS", uitleg: "Gediskwalificeerd" },
  GESTAAKT: { tekst: "Gestaakt", uitleg: "Rit gestaakt" },
  NEUTR: { tekst: "Neutraal", uitleg: "Neutrale rit, telt niet mee" },
  DEMO: { tekst: "Demo", uitleg: "Demonstratierit" },
  // Zeldzaam - 6 keer in 52.478 Farm Stock-rijen - maar de app liet de
  // afkorting nooit los. Joost merkte dat terecht op.
  BM: { tekst: "BM", uitleg: "Buiten mededinging - telt niet voor de competitie" },
};

// Waarden die "geen uitslag" betekenen.
const LEEG = new Set(["", "--", "-", "-.-", "..", "."]);

function normaliseer(ruw: string): string {
  // Punten, spaties en tabs eruit: "F.P." en "d.n.s." worden FP en DNS.
  return ruw.replace(/[.\s ]/g, "").toUpperCase();
}

// Leest één ritwaarde uit TRS.
export function leesResultaat(ruw: string | null | undefined): Resultaat | null {
  const s = (ruw ?? "").replace(/\s+/g, " ").trim();
  if (LEEG.has(s) || LEEG.has(normaliseer(s))) return null;

  const kaal = normaliseer(s);

  // Full Pull wint van een meegeleverde afstand: "FP/90.42" is een Full Pull.
  if (kaal === "FP" || kaal.startsWith("FP/") || kaal.startsWith("FULLPULL")) {
    return { soort: "fullpull", tekst: "Full Pull" };
  }

  const status = STATUS[kaal];
  if (status) return { soort: "status", ...status };

  // Afstand. TRS bevat komma's, losse spaties en soms een voorvoegsel als
  // "PT64.56" of een url-encoded "%20", dus we vissen het getal eruit.
  const getal = s.replace(",", ".").replace(/%20/g, "").match(/\d+(?:\.\d+)?/);
  if (getal) {
    const meters = parseFloat(getal[0]);
    if (Number.isFinite(meters)) {
      return { soort: "afstand", meters, tekst: `${meters.toFixed(2)} m` };
    }
  }

  // Onbekende code: liever tonen wat er staat dan een streepje.
  return { soort: "status", tekst: s, uitleg: s };
}

// De kwalificatie-uitslag uit run1 en run2.
//
// Run 2 is een herkansing en komt niet vaak voor, maar wel in vaste situaties:
// aan het begin van een klasse rijdt de eerste deelnemer een proeftrek, en als
// de sleepwagen zwaarder gezet wordt schuift die proeftrek door naar de
// volgende. Wie de trek rijdt waarmee de wedstrijdleiding besluit dat de
// sleepwagen goed staat, mag kiezen: de afstand laten staan, of opnieuw
// trekken. Ook wie vóór de 30 meter stopt mag opnieuw.
//
// Kiest een deelnemer voor een herkansing, dan telt díe poging - ook als die
// slechter uitpakt. Run 2 vervangt run 1 dus, het is niet de beste van twee.
// Dat gaat vaak mis: van de 981 keer dat beide pogingen een afstand opleverden
// was de tweede 537 keer beter en 441 keer slechter. We houden de eerste poging
// daarom bij, zodat een kijker die een hoger getal zag begrijpt wat er gebeurde.
//
// Waaróm er een herkansing was legt TRS niet vast: de vier situaties hierboven
// leveren allemaal simpelweg een gevulde run2 op. We noemen het dus "2e poging"
// en niet "proeftrek" - dat laatste zou een gok zijn.
export type Ritten = {
  /** Wat er in de kwalificatiekolom komt te staan. */
  kwalificatie: Resultaat | null;
  /** Kleine regel eronder: de vervallen poging, of de losse ritten bij een totaal. */
  toelichting: string | null;
  /** De afsluitende rit: een finale, of bij enkele wedstrijden een totaal. */
  slot: Resultaat | null;
  slotLabel: "Finale" | "Totaal";
};

// Leest de drie runkolommen van één deelnemer uit.
//
// `run2IsRegel` geeft aan of vrijwel iedereen in de klasse een tweede rit
// heeft. Dan is het geen herkansing maar een ronde die bij het wedstrijdsysteem
// hoort, en noemen we het niet zo. TRS legt het systeem wel vast
// (`klassenperweds.wedstrijd_systeem` met de bijbehorende afstanden), maar wat
// de codes 0 t/m 6 betekenen is bij ons niet bekend - navragen bij Gerrit.
// Tot die tijd interpreteren we niet meer dan we kunnen onderbouwen.
export function leesRitten(
  run1: string | null | undefined,
  run2: string | null | undefined,
  run3: string | null | undefined,
  run2IsRegel = false,
): Ritten {
  const eerste = leesResultaat(run1);
  const tweede = leesResultaat(run2);
  const derde = leesResultaat(run3);

  // Uitzondering: bij Ysselsteyn (zo) telt TRS run1 en run2 op in run3 in
  // plaats van er een finale in te zetten. Dat gaat om 118 rijen uit twee
  // wedstrijden, tegenover 11.730 rijen volgens het finale-model, dus het is
  // een lokale afwijking en geen tweede systeem. Zonder deze controle zouden
  // we daar een opgeteld getal als "finale" presenteren.
  if (
    eerste?.soort === "afstand" &&
    tweede?.soort === "afstand" &&
    derde?.soort === "afstand" &&
    Math.abs(derde.meters - (eerste.meters + tweede.meters)) < 0.05
  ) {
    return {
      kwalificatie: null,
      toelichting: `1e ${eerste.tekst} · 2e ${tweede.tekst}`,
      slot: derde,
      slotLabel: "Totaal",
    };
  }

  if (tweede) {
    // Hoort de tweede rit bij het systeem, dan tonen we beide ritten zonder er
    // een naam aan te geven. Is hij uitzondering, dan is het een herkansing en
    // verviel de eerste poging - ook als die beter was.
    return run2IsRegel
      ? {
          // Twee ritten die allebei tellen: de béste is de uitslag, niet de
          // laatste. Bij Stroe stond Yellow Nightmare eerste met 109.31 uit de
          // eerste rit en 103.97 uit de tweede; de tweede tonen sprak de
          // positie tegen. De rangschikking in TRS volgt daar de beste van de
          // twee, dus dat doen wij ook.
          //
          // Onderbouwd op één klasse. Wat de codes in
          // `klassenperweds.wedstrijd_systeem` betekenen weten we nog niet -
          // die vraag staat uit bij Gerrit. Zodra dat helder is hoort dit
          // daaruit te volgen in plaats van uit de vorm van de gegevens.
          kwalificatie: besteResultaat([run1, run2]) ?? tweede,
          toelichting: eerste ? `1e ${eerste.tekst} · 2e ${tweede.tekst}` : null,
          slot: derde,
          slotLabel: "Finale",
        }
      : {
          kwalificatie: tweede,
          toelichting: eerste ? `2e poging · 1e ${eerste.tekst}` : "2e poging",
          slot: derde,
          slotLabel: "Finale",
        };
  }

  return { kwalificatie: eerste, toelichting: null, slot: derde, slotLabel: "Finale" };
}

// De rit die de uitslag bepaalt, voor plekken waar maar één waarde past.
export function beslissendeRit(r: Ritten): Resultaat | null {
  return r.slot ?? r.kwalificatie;
}

// Beste van meerdere ritten: Full Pull wint, daarna de verste afstand. Een
// status telt alleen mee als er verder niets is.
export function besteResultaat(runs: (string | null | undefined)[]): Resultaat | null {
  const gelezen = runs.map(leesResultaat).filter((r): r is Resultaat => r !== null);
  if (gelezen.length === 0) return null;

  const fullPull = gelezen.find((r) => r.soort === "fullpull");
  if (fullPull) return fullPull;

  const afstanden = gelezen.filter((r): r is Extract<Resultaat, { soort: "afstand" }> => r.soort === "afstand");
  if (afstanden.length > 0) {
    return afstanden.reduce((a, b) => (b.meters > a.meters ? b : a));
  }

  return gelezen[0];
}

